import { useState } from 'react';
import { ExportState } from '../types';
import type { AdResult } from '../../../mocks/adResults';

const PLATFORM_LABELS: Record<string, string> = {
  facebook_feed: 'Facebook Feed',
  instagram_feed: 'Instagram Feed',
  instagram_story: 'Instagram Story',
  meta_ad: 'Meta Ad',
  google_display: 'Google Display',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
};

const SIZE_LABELS: Record<string, string> = {
  square: 'Square (1080×1080)',
  portrait: 'Portrait (1080×1350)',
  story: 'Story (1080×1920)',
  landscape: 'Landscape (1200×628)',
};

const FORMAT_LABELS: Record<string, string> = {
  static: 'Static Image',
  video: 'Video Export',
  pack: 'Full Export Pack',
};

const PLATFORM_ICONS: Record<string, string> = {
  facebook_feed: 'ri-facebook-circle-line',
  instagram_feed: 'ri-instagram-line',
  instagram_story: 'ri-instagram-line',
  meta_ad: 'ri-facebook-box-line',
  google_display: 'ri-google-line',
  tiktok: 'ri-tiktok-line',
  linkedin: 'ri-linkedin-box-line',
};

function buildFileList(state: ExportState): string[] {
  const files: string[] = [];
  const { selectedSizes, exportFormat, includedContent } = state;

  if (includedContent.includes('image')) {
    selectedSizes.forEach((s) => {
      const ext = exportFormat === 'video' ? 'mp4' : 'png';
      files.push(`ad-${s}.${ext}`);
    });
  }
  if (includedContent.includes('video') && exportFormat !== 'static') {
    files.push('ad-animated.mp4');
  }
  if (exportFormat === 'pack') {
    if (includedContent.some((c) => ['headline', 'primary_text', 'subheadline', 'cta', 'caption', 'hashtags'].includes(c))) {
      files.push('ad-copy.txt');
    }
    files.push('platform-instructions.txt');
  }
  if (includedContent.includes('brand')) {
    files.push('brand-assets.zip');
  }
  return files.length > 0 ? files : ['ad-creative.png'];
}

const FILE_ICONS: Record<string, string> = {
  png: 'ri-image-2-line',
  jpg: 'ri-image-line',
  mp4: 'ri-video-line',
  txt: 'ri-file-text-line',
  zip: 'ri-folder-zip-line',
};

function getFileIcon(name: string) {
  const ext = name.split('.').pop() ?? 'txt';
  return FILE_ICONS[ext] ?? 'ri-file-line';
}

function getFileColor(name: string) {
  const ext = name.split('.').pop() ?? '';
  if (ext === 'mp4') return 'text-purple-400';
  if (ext === 'png' || ext === 'jpg') return 'text-blue-400';
  if (ext === 'zip') return 'text-amber-400';
  return 'text-stone-400';
}

// Platform Preview Frame Components
function FacebookFrame({ imageUrl, headline, cta }: { imageUrl?: string; headline?: string; cta?: string }) {
  return (
    <div className="bg-white rounded-xl overflow-hidden w-full shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2 bg-white">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center">
          <i className="ri-building-2-line text-white text-xs" />
        </div>
        <div>
          <div className="text-xs font-semibold text-stone-800 leading-none">Your Brand</div>
          <div className="text-xs text-stone-400 leading-none mt-0.5">Sponsored</div>
        </div>
      </div>
      <div className="w-full aspect-square bg-stone-100 relative overflow-hidden">
        {imageUrl ? (
          <img src={imageUrl} alt="Ad preview" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-amber-400/20 flex items-center justify-center">
            <span className="text-stone-400 text-xs">1080 × 1080</span>
          </div>
        )}
      </div>
      <div className="px-3 py-2.5 bg-white">
        <div className="text-xs font-semibold text-stone-800 truncate">{headline || 'Your headline goes here'}</div>
        <div className="text-xs text-stone-500 truncate mt-0.5">Your primary ad copy text</div>
        <div className="mt-2 bg-stone-100 rounded-md px-3 py-1.5 text-center text-xs font-semibold text-stone-700">
          {cta || 'Learn More'}
        </div>
      </div>
    </div>
  );
}

function StoryFrame({ imageUrl, cta }: { imageUrl?: string; cta?: string }) {
  return (
    <div className="bg-black rounded-xl overflow-hidden w-full shadow-sm" style={{ aspectRatio: '9/16' }}>
      <div className="relative w-full h-full">
        {imageUrl ? (
          <img src={imageUrl} alt="Story preview" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900 to-stone-950 flex items-center justify-center">
            <span className="text-stone-600 text-xs">1080 × 1920</span>
          </div>
        )}
        <div className="absolute top-0 left-0 right-0 flex gap-1 px-2 pt-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className={`h-0.5 flex-1 rounded-full ${i === 0 ? 'bg-white' : 'bg-white/30'}`} />
          ))}
        </div>
        <div className="absolute top-4 left-2 flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
            <i className="ri-building-2-line text-white text-xs" />
          </div>
          <span className="text-white text-xs font-medium">Your Brand</span>
        </div>
        <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-2">
          <div className="bg-orange-500 rounded-full px-4 py-1.5 text-white text-xs font-bold">
            {cta || 'Shop Now'}
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleFrame({ imageUrl, headline, cta }: { imageUrl?: string; headline?: string; cta?: string }) {
  return (
    <div className="bg-white rounded-xl overflow-hidden w-full border border-stone-200 shadow-sm">
      <div className="w-full" style={{ aspectRatio: '300/250' }}>
        <div className="w-full h-full bg-stone-100 relative overflow-hidden">
          {imageUrl ? (
            <img src={imageUrl} alt="Display preview" className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-amber-400/20 flex items-center justify-center">
              <span className="text-stone-400 text-xs">300 × 250</span>
            </div>
          )}
          {(headline || cta) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-3 bg-black/30">
              {headline && <div className="text-white text-xs font-bold text-center mb-2">{headline}</div>}
              {cta && <div className="bg-white/20 rounded px-3 py-1 text-white text-xs font-semibold">{cta}</div>}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between px-2 py-1.5 bg-white border-t border-stone-100">
        <span className="text-xs text-stone-400">yoursite.com</span>
        <span className="text-xs bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">Ad</span>
      </div>
    </div>
  );
}

interface ExportPreviewPanelProps {
  state: ExportState;
  adResult: AdResult | null;
  editedUrls: Record<number, string>;
}

export default function ExportPreviewPanel({ state, adResult, editedUrls }: ExportPreviewPanelProps) {
  const files = buildFileList(state);
  const totalFiles = files.length;
  const platforms = state.selectedPlatforms.length;
  const sizes = state.selectedSizes.length;

  // Get the active image URL from the ad result
  const activeImageUrl = editedUrls[0] || adResult?.imageUrls?.[0] || adResult?.imageUrl || undefined;
  const headline = adResult?.headlines?.[0] || adResult?.overlayText?.headline;
  const cta = adResult?.ctas?.[0] || adResult?.overlayText?.cta;

  // Platform preview visibility
  const showFacebook = state.selectedPlatforms.includes('facebook_feed') || state.selectedPlatforms.includes('instagram_feed') || state.selectedPlatforms.includes('meta_ad');
  const showStory = state.selectedPlatforms.includes('instagram_story') || state.selectedPlatforms.includes('tiktok');
  const showGoogle = state.selectedPlatforms.includes('google_display');

  return (
    <div className="sticky top-4 flex flex-col gap-4">
      {/* Selected Creative Preview */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-image-line text-orange-400 text-sm" />
            </div>
            <span className="text-sm font-semibold text-white">Selected Creative</span>
          </div>
          <span className="text-xs text-stone-500">{adResult?.businessName || 'Your Ad'}</span>
        </div>
        <div className="p-4">
          <div className="aspect-square rounded-lg overflow-hidden bg-stone-800">
            {activeImageUrl ? (
              <img 
                src={activeImageUrl} 
                alt="Selected creative" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <i className="ri-image-line text-stone-700 text-4xl" />
              </div>
            )}
          </div>
          <div className="mt-3 space-y-1">
            <div className="text-sm font-medium text-white truncate">{headline || 'Your Headline'}</div>
            <div className="text-xs text-stone-500 truncate">{cta || 'Call to Action'}</div>
          </div>
        </div>
      </div>

      {/* Platform Previews */}
      {(showFacebook || showStory || showGoogle) && (
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-eye-line text-orange-400 text-sm" />
            </div>
            <span className="text-sm font-semibold text-white">Platform Previews</span>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {showFacebook && (
              <div className="space-y-1">
                <FacebookFrame imageUrl={activeImageUrl} headline={headline} cta={cta} />
                <div className="text-center text-xs text-stone-500">Feed</div>
              </div>
            )}
            {showStory && (
              <div className="space-y-1">
                <StoryFrame imageUrl={activeImageUrl} cta={cta} />
                <div className="text-center text-xs text-stone-500">Story</div>
              </div>
            )}
            {showGoogle && (
              <div className="space-y-1">
                <GoogleFrame imageUrl={activeImageUrl} headline={headline} cta={cta} />
                <div className="text-center text-xs text-stone-500">Display</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export Summary */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-file-list-line text-orange-400 text-sm" />
          </div>
          <span className="text-sm font-semibold text-white">Export Summary</span>
        </div>

        <div className="space-y-3">
          {/* Platforms */}
          <div>
            <div className="text-xs text-stone-500 mb-2">Platforms ({platforms})</div>
            <div className="flex flex-wrap gap-1.5">
              {state.selectedPlatforms.map((p) => (
                <span 
                  key={p} 
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-stone-800 border border-stone-700 text-xs text-stone-300"
                >
                  <i className={`${PLATFORM_ICONS[p]} text-stone-400`} />
                  {PLATFORM_LABELS[p]}
                </span>
              ))}
            </div>
          </div>

          {/* Sizes */}
          <div>
            <div className="text-xs text-stone-500 mb-2">Sizes ({sizes})</div>
            <div className="flex flex-wrap gap-1.5">
              {state.selectedSizes.map((s) => (
                <span 
                  key={s} 
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-stone-800 border border-stone-700 text-xs text-stone-300"
                >
                  <i className="ri-aspect-ratio-line text-stone-400" />
                  {SIZE_LABELS[s].split(' ')[0]}
                </span>
              ))}
            </div>
          </div>

          {/* Format */}
          <div className="flex items-center justify-between py-2 border-t border-stone-800">
            <span className="text-xs text-stone-500">Format</span>
            <span className="text-xs font-medium text-white">{FORMAT_LABELS[state.exportFormat]}</span>
          </div>

          {/* Total Files */}
          <div className="flex items-center justify-between py-2 border-t border-stone-800">
            <span className="text-xs text-stone-500">Total Files</span>
            <span className="text-sm font-bold text-orange-400">{totalFiles}</span>
          </div>
        </div>
      </div>

      {/* Package Contents */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-folder-zip-line text-orange-400 text-sm" />
          </div>
          <span className="text-sm font-semibold text-white">Package Contents</span>
        </div>

        <div className="space-y-1.5">
          {files.map((file) => (
            <div 
              key={file} 
              className="flex items-center gap-3 px-3 py-2 bg-stone-800/50 rounded-lg border border-stone-800/50"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${getFileIcon(file)} text-sm ${getFileColor(file)}`} />
              </div>
              <span className="text-xs text-stone-300 font-mono flex-1 truncate">{file}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-500/20 shrink-0">
            <i className="ri-lightbulb-line text-orange-400 text-sm" />
          </div>
          <div>
            <div className="text-sm font-medium text-white mb-1">Quick Tips</div>
            <ul className="space-y-1.5">
              <li className="text-xs text-stone-400 flex items-start gap-2">
                <i className="ri-check-line text-orange-400 mt-0.5 shrink-0" />
                Square (1:1) works best for feed placements
              </li>
              <li className="text-xs text-stone-400 flex items-start gap-2">
                <i className="ri-check-line text-orange-400 mt-0.5 shrink-0" />
                Vertical (9:16) is required for Stories and Reels
              </li>
              <li className="text-xs text-stone-400 flex items-start gap-2">
                <i className="ri-check-line text-orange-400 mt-0.5 shrink-0" />
                Landscape (16:9) performs well on Google Display
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
